// Work Areas and Categories for Lawyer Specialties

// -----------------------------------------------------------------------------
// Canonical work-area definitions (single source of truth for the entire app)
// -----------------------------------------------------------------------------

export const WORK_AREAS = {
  CIVIL: "Casos Civiles",
  FAMILY: "Familia y Menores",
  PERSONAL_INJURY: "Accidentes y Daños Personales",
  CRIMINAL: "Casos Criminales y Órdenes de Protección",
  NOTARIAL: "Servicios Notariales",
  LABOR: "Casos Laborales",
  CORPORATE: "Negocios y Corporaciones",
  INTELLECTUAL_PROPERTY: "Propiedad Intelectual",
  REAL_ESTATE: "Bienes Raíces y Vivienda",
  CONSUMER: "Consumidores",
  IMMIGRATION: "Inmigración",
  TAX: "Impuestos y Finanzas",
  ADMINISTRATIVE: "Derecho Administrativo",
  SOCIAL_SECURITY: "Seguridad Social y Beneficios",
  TECHNOLOGY: "Tecnología y Privacidad",
  TELECOMMUNICATIONS: "Telecomunicaciones",
  ADA: "Ley ADA y Discapacidad",
  MARITIME: "Asunto Marítimo",
  MUNICIPAL: "Asuntos Municipales",
  MEDIATION: "Mediación y Arbitraje",
  FEDERAL_LITIGATION: "Casos Federales",
  INSURANCE: "Reclamaciones de Seguros",
  BANKRUPTCY_RESTRUCTURING: "Quiebras y Reestructuración Financiera",
  CONSTRUCTION_CONTRACTS: "Construcción y Contratos",
  ENVIRONMENTAL_LAND_USE: "Derecho Ambiental y Uso de Terrenos",
  BANKING_FINANCE: "Asunto Bancario y Financiero",
  SPORTS_ENTERTAINMENT: "Deportes y Entretenimiento",
  CANNABIS_REGULATED_BUSINESS: "Cannabis y Negocios Regulados",
  MILITARY_VETERANS: "Derecho Militar y Veteranos",
  ELDER_LAW: "Adultos Mayores",
  CIVIL_CONSTITUTIONAL_RIGHTS: "Derechos Civiles y Constitucionales",
  APPEALS: "Apelaciones",
} as const;

export type WorkArea = (typeof WORK_AREAS)[keyof typeof WORK_AREAS];
type WorkAreaKey = keyof typeof WORK_AREAS;

const WORK_AREA_SEARCH_ALIASES: Partial<
  Record<WorkAreaKey, readonly string[]>
> = {
  CIVIL: ["Derecho Civil"],
  FAMILY: ["Derecho de Familia"],
  CRIMINAL: ["Derecho Penal"],
  LABOR: ["Derecho Laboral"],
  CORPORATE: ["Empresarismo y Corporaciones"],
  MARITIME: ["Derecho Marítimo"],
  FEDERAL_LITIGATION: ["Litigio Federal"],
  INSURANCE: ["Derecho de Seguros"],
  BANKING_FINANCE: ["Derecho Bancario y Financiero"],
};

const getWorkAreaKey = (workAreaKey: string): WorkAreaKey | undefined => {
  const upperKey = workAreaKey.toUpperCase().replace(/-/g, "_") as WorkAreaKey;
  if (upperKey in WORK_AREAS) {
    return upperKey;
  }

  const currentLabelKey = Object.entries(WORK_AREAS).find(
    ([, value]) => value === workAreaKey,
  )?.[0] as WorkAreaKey | undefined;
  if (currentLabelKey) {
    return currentLabelKey;
  }

  return Object.entries(WORK_AREA_SEARCH_ALIASES).find(([, aliases]) =>
    aliases?.includes(workAreaKey),
  )?.[0] as WorkAreaKey | undefined;
};

// Detailed configuration: descriptions and sub-categories for each main area
export const WORK_AREA_CONFIG = {
  [WORK_AREAS.CIVIL]: {
    label: "Casos Civiles",
    description: "Contratos, daños, responsabilidad civil",
    subcategories: [
      "Contratos",
      "Cobro de Dinero",
      "Daños y Perjuicios",
      "Problemas entre Vecinos",
      "Cambio o Corrección de Nombre",
      "Otros asuntos",
    ],
  },
  [WORK_AREAS.MEDIATION]: {
    label: "Mediación y Arbitraje",
    description: "Métodos alternativos de resolución de conflictos",
    subcategories: ["Mediación", "Arbitraje"],
  },
  [WORK_AREAS.FAMILY]: {
    label: "Familia y Menores",
    description: "Divorcios, custodia, adopciones",
    subcategories: [
      "Divorcio",
      "Custodia y Patria Potestad",
      "Pensiones Alimentarias",
      "Adopción",
      "Capitulaciones Matrimoniales",
      "Orden de Protección",
      "Otros asuntos",
    ],
  },
  [WORK_AREAS.PERSONAL_INJURY]: {
    label: "Accidentes y Daños Personales",
    description: "Lesiones personales y responsabilidad civil",
    subcategories: [
      "Accidente de Auto",
      "Lesión y Caída",
      "Negligencia Médica",
      "Responsabilidad Civil",
      "Otros asuntos",
    ],
  },
  [WORK_AREAS.CRIMINAL]: {
    label: "Casos Criminales y Órdenes de Protección",
    description: "Defensa criminal, delitos",
    subcategories: [
      "Órdenes de Protección",
      "Delitos Graves",
      "Delitos Menos Graves",
      "Otros asuntos",
    ],
  },
  [WORK_AREAS.NOTARIAL]: {
    label: "Servicios Notariales",
    description: "Documentos notariales y certificaciones",
    subcategories: [
      "Testamentos y Herencias",
      "Poderes y Declaraciones Juradas",
      "Capitulaciones",
      "Boda ante Notario",
      "Autorizaciones para Menores",
      "Otros asuntos",
    ],
  },
  [WORK_AREAS.LABOR]: {
    label: "Casos Laborales",
    description: "Relaciones laborales, despidos",
    subcategories: [
      "Despido Injustificado",
      "Hostigamiento Laboral",
      "Acoso Sexual",
      "Discriminación",
      "Salarios y Horas",
      "Incapacidades",
      "Otros asuntos",
    ],
  },
  [WORK_AREAS.CORPORATE]: {
    label: "Negocios y Corporaciones",
    description: "Empresas, fusiones, contratos comerciales",
    subcategories: [
      "Constitución y Registro de Empresas",
      "Permisos y Licencias",
      "Contratos Comerciales",
      "Litigios Comerciales",
      "Incentivos Gubernamentales",
      "Otros asuntos",
    ],
  },
  [WORK_AREAS.FEDERAL_LITIGATION]: {
    label: "Casos Federales",
    description: "Casos, demandas y apelaciones en foros federales",
    subcategories: [
      "Casos Civiles",
      "Casos Criminales",
      "Litigio Comercial Federal",
      "Litigio Laboral Federal",
      "Litigio de Propiedad Intelectual",
      "Demandas Contra el Gobierno Federal",
      "Injunctions Federales",
      "Habeas Corpus",
      "Apelaciones Federales",
      "Federal Tort Claims",
      "Litigios Constitucionales",
      "Otros asuntos",
    ],
  },
  [WORK_AREAS.INSURANCE]: {
    label: "Reclamaciones de Seguros",
    description: "Reclamaciones, cubiertas y disputas con aseguradoras",
    subcategories: [
      "Reclamaciones de Seguros",
      "Negativa de Cubierta",
      "Seguros de Propiedad",
      "Seguros de Salud",
      "Seguros de Vida",
      "Seguros Comerciales",
      "Ajustes y Apelaciones",
      "Reclamaciones por Huracanes",
      "Bad Faith Insurance",
      "Otros asuntos",
    ],
  },
  [WORK_AREAS.BANKRUPTCY_RESTRUCTURING]: {
    label: "Quiebras y Reestructuración Financiera",
    description: "Quiebras, reestructuración y protección financiera",
    subcategories: [
      "Capítulo 7",
      "Capítulo 11",
      "Capítulo 13",
      "Reestructuración Comercial",
      "Negociación de Deudas",
      "Ejecuciones y Embargos",
      "Cobro de Dinero",
      "Protección de Activos",
      "Otros asuntos",
    ],
  },
  [WORK_AREAS.CONSTRUCTION_CONTRACTS]: {
    label: "Construcción y Contratos",
    description: "Contratos, permisos y disputas de construcción",
    subcategories: [
      "Contratos de Construcción",
      "Defectos de Construcción",
      "Incumplimiento de Contrato",
      "Litigios de Construcción",
      "Cobro a Contratistas",
      "Gravámenes y Liens",
      "Permisos de Construcción",
      "Desarrollo de Proyectos",
      "Otros asuntos",
    ],
  },
  [WORK_AREAS.ENVIRONMENTAL_LAND_USE]: {
    label: "Derecho Ambiental y Uso de Terrenos",
    description: "Permisos, cumplimiento ambiental y uso de terrenos",
    subcategories: [
      "Permisos Ambientales",
      "Uso de Terrenos",
      "Recursos Naturales",
      "Endosos Gubernamentales",
      "Cumplimiento Ambiental",
      "Querellas Ambientales",
      "Impacto Ambiental",
      "Zonificación",
      "Otros asuntos",
    ],
  },
  [WORK_AREAS.BANKING_FINANCE]: {
    label: "Asunto Bancario y Financiero",
    description: "Préstamos, ejecuciones y regulación financiera",
    subcategories: [
      "Ejecuciones Hipotecarias",
      "Préstamos Comerciales",
      "Refinanciamientos",
      "Regulación Financiera",
      "Fraude Bancario",
      "Cobro Bancario",
      "Cartas de Crédito",
      "Otros asuntos",
    ],
  },
  [WORK_AREAS.SPORTS_ENTERTAINMENT]: {
    label: "Deportes y Entretenimiento",
    description: "Contratos, derechos y representación artística o deportiva",
    subcategories: [
      "Contratos Deportivos",
      "Contratos Artísticos",
      "Manejo de Imagen",
      "Regalías",
      "Producción de Eventos",
      "Influencers y Redes Sociales",
      "Derechos de Autor",
      "Representación Artística",
      "Otros asuntos",
    ],
  },
  [WORK_AREAS.CANNABIS_REGULATED_BUSINESS]: {
    label: "Cannabis y Negocios Regulados",
    description: "Licencias, permisos y cumplimiento para negocios regulados",
    subcategories: [
      "Licencias Cannabis Medicinal",
      "Dispensarios",
      "Cultivo y Manufactura",
      "Cumplimiento Regulatorio",
      "Renovaciones",
      "Auditorías",
      "Permisos Especiales",
      "Otros asuntos",
    ],
  },
  [WORK_AREAS.MILITARY_VETERANS]: {
    label: "Derecho Militar y Veteranos",
    description: "Beneficios, reclamaciones y derechos militares",
    subcategories: [
      "Beneficios para Veteranos",
      "Reclamaciones VA",
      "Descargas Militares",
      "Derechos Laborales Militares",
      "Discrimen Militar",
      "Apelaciones Administrativas",
      "Otros asuntos",
    ],
  },
  [WORK_AREAS.ELDER_LAW]: {
    label: "Adultos Mayores",
    description: "Planificación, beneficios y protección de adultos mayores",
    subcategories: [
      "Planificación Patrimonial",
      "Tutelas",
      "Poderes Duraderos",
      "Medicare y Medicaid",
      "Égidas y Hogares",
      "Maltrato de Adultos Mayores",
      "Administración de Bienes",
      "Otros asuntos",
    ],
  },
  [WORK_AREAS.CIVIL_CONSTITUTIONAL_RIGHTS]: {
    label: "Derechos Civiles y Constitucionales",
    description: "Derechos civiles, humanos y garantías constitucionales",
    subcategories: [
      "Brutalidad Policial",
      "Libertad de Expresión",
      "Violaciones Constitucionales",
      "Discrimen",
      "Derechos de Confinados",
      "Due Process",
      "Equal Protection",
      "Derechos Humanos",
      "Otros asuntos",
    ],
  },
  [WORK_AREAS.APPEALS]: {
    label: "Apelaciones",
    description: "Recursos apelativos, revisión judicial y certiorari",
    subcategories: [
      "Tribunal Apelativo",
      "Tribunal Supremo de Puerto Rico",
      "Recursos Extraordinarios",
      "Certiorari",
      "Revisión Judicial",
      "Otros asuntos",
    ],
  },
  [WORK_AREAS.INTELLECTUAL_PROPERTY]: {
    label: "Propiedad Intelectual",
    description: "Marcas, patentes, derechos de autor",
    subcategories: [
      "Registro de Marcas",
      "Derechos de Autor",
      "Patentes",
      "Secretos Comerciales",
      "Contratos de Licencia",
      "Entretenimiento y Medios",
      "Otros asuntos",
    ],
  },
  [WORK_AREAS.REAL_ESTATE]: {
    label: "Bienes Raíces y Vivienda",
    description: "Compraventa, arrendamientos",
    subcategories: [
      "Arrendamientos",
      "Compra y Venta",
      "Títulos y Escrituras",
      "Condominio y Urbanización",
      "Ejecuciones Hipotecarias",
      "Desahucios",
      "Otros asuntos",
    ],
  },
  [WORK_AREAS.CONSUMER]: {
    label: "Consumidores",
    description: "Protección al consumidor",
    subcategories: [
      "Garantías",
      "Reclamaciones",
      "Publicidad Engañosa",
      "Productos Defectuosos",
      "Otros asuntos",
    ],
  },
  [WORK_AREAS.IMMIGRATION]: {
    label: "Inmigración",
    description: "Visas, residencias, ciudadanía",
    subcategories: [
      "Ciudadanía",
      "Visas",
      "Residencias",
      "Procesos de Deportación",
      "Otros asuntos",
    ],
  },
  [WORK_AREAS.TAX]: {
    label: "Impuestos y Finanzas",
    description: "Impuestos, tributación",
    subcategories: [
      "Planillas y Obligaciones",
      "Deudas Contributivas",
      "Exenciones y Créditos",
      "Banca y Finanzas",
      "Quiebra Personal o Comercial",
      "Otros asuntos",
    ],
  },
  [WORK_AREAS.ADMINISTRATIVE]: {
    label: "Derecho Administrativo",
    description: "Procedimientos administrativos",
    subcategories: [
      "Reclamaciones contra Agencias",
      "Procedimientos ante Juntas Administrativas",
      "Permisos Especiales",
      "Otros asuntos",
    ],
  },
  [WORK_AREAS.SOCIAL_SECURITY]: {
    label: "Seguridad Social y Beneficios",
    description: "Beneficios sociales y seguridad social",
    subcategories: [
      "Seguro Social por Incapacidad",
      "Asistencia del Gobierno",
      "Apelaciones de Beneficios",
      "Otros asuntos",
    ],
  },
  [WORK_AREAS.TECHNOLOGY]: {
    label: "Tecnología y Privacidad",
    description: "Protección de datos y privacidad digital",
    subcategories: [
      "Protección de Datos Personales",
      "Privacidad en Línea",
      "Ciberacoso",
      "Comercio Electrónico",
      "Otros asuntos",
    ],
  },
  [WORK_AREAS.TELECOMMUNICATIONS]: {
    label: "Telecomunicaciones",
    description: "Servicios de telecomunicaciones",
    subcategories: [
      "Contratos de Servicios",
      "Reclamaciones contra Proveedores",
      "Publicidad o Cobros",
      "Otros asuntos",
    ],
  },
  [WORK_AREAS.ADA]: {
    label: "Ley ADA y Discapacidad",
    description: "Acceso y derechos de discapacidad",
    subcategories: [
      "Accesibilidad",
      "Discriminación por Discapacidad",
      "Acomodaciones Razonables",
      "Otros asuntos",
    ],
  },
  [WORK_AREAS.MARITIME]: {
    label: "Asunto Marítimo",
    description: "Navegación, comercio marítimo",
    subcategories: [
      "Accidentes Marítimos",
      "Contratos de Fletamento",
      "Salvamento Marítimo",
      "Otros asuntos",
    ],
  },
  [WORK_AREAS.MUNICIPAL]: {
    label: "Asuntos Municipales",
    description: "Reglamentos y ordenanzas locales",
    subcategories: [
      "Permisos Municipales",
      "Ordenanzas Locales",
      "Impuestos Municipales",
      "Otros asuntos",
    ],
  },
} as const;

// Utility function to get work area label from key or value
export const getWorkAreaLabel = (workAreaKey: string): string => {
  const matchedKey = getWorkAreaKey(workAreaKey);
  const workAreaEnum = matchedKey ? WORK_AREAS[matchedKey] : undefined;

  return workAreaEnum || workAreaKey;
};

export const getWorkAreaSearchLabels = (workAreaKey: string): string[] => {
  const displayLabel = getWorkAreaLabel(workAreaKey);
  const matchedKey = getWorkAreaKey(workAreaKey);

  const aliases = matchedKey ? WORK_AREA_SEARCH_ALIASES[matchedKey] || [] : [];

  return Array.from(new Set([displayLabel, ...aliases]));
};

export const getWorkAreaConfig = (workAreaKey: string) =>
  WORK_AREA_CONFIG[
    getWorkAreaLabel(workAreaKey) as keyof typeof WORK_AREA_CONFIG
  ];
